import type { StateCreator } from 'zustand';
import type { GroupsState } from '../useBoundStore';
import {
  filterDeletedGroupSources,
  updateLibraryPlan,
} from '../../settings/planStudio/profilePlanLibraryUtils';
import {
  archiveGroupPlan,
  archiveProfilePlan,
  listProfilePlanSources,
  updateGroupPlanStatus,
  updateProfilePlanAutoGenerationSettings,
  updateProfilePlanSourceEnabled,
  updateProfilePlanStatus,
} from '../../settings/planStudio/api';
import { profilePlanListRequest } from '../../settings/planStudio/profilePlanListViewUtils';
import type {
  PlanSourceType,
  PlanStudioPlanStatus,
  PlanStudioSavedPlan,
  ProfilePlanLibrary,
} from '../../settings/planStudio/types';

export type ProfilePlanLibraryView = PlanStudioPlanStatus | 'history';

export interface ProfilePlansState {
  profilePlanLibrary: ProfilePlanLibrary | null;
  profilePlanLibraryStatusFilter: ProfilePlanLibraryView;
  profilePlanLibraryLoadedProfileId: string | null;
  profilePlanLibraryLoading: boolean;
  profilePlanLibrarySavingSourceId: string | null;
  profilePlanLibraryUpdatingPlanId: string | null;
  profilePlanLibraryArchivingPlanId: string | null;
  profilePlanLibrarySavingAutoGeneration: boolean;
  profilePlanLibraryError: string | null;

  fetchProfilePlanLibrary: (
    profileId: string,
    view?: ProfilePlanLibraryView
  ) => Promise<ProfilePlanLibrary>;
  setProfilePlanAutoGeneration: (
    profileId: string,
    enabled: boolean
  ) => Promise<boolean>;
  setProfileGroupPlanSourceEnabled: (
    profileId: string,
    groupId: string,
    enabled: boolean
  ) => Promise<void>;
  updateProfileLibraryPlanStatus: (
    targetType: PlanSourceType,
    targetId: string,
    planId: string,
    status: PlanStudioPlanStatus
  ) => Promise<PlanStudioSavedPlan>;
  archiveProfileLibraryPlan: (
    targetType: PlanSourceType,
    targetId: string,
    planId: string
  ) => Promise<void>;
  applyProfileLibraryPlan: (
    plan: PlanStudioSavedPlan,
    view?: ProfilePlanLibraryView
  ) => void;
  clearPlansState: () => void;
}

const DEFAULT_PROFILE_PLAN_LIBRARY_VIEW: ProfilePlanLibraryView = 'active';

const removeLibraryPlan = (
  library: ProfilePlanLibrary | null,
  targetType: PlanSourceType,
  targetId: string,
  planId: string
): ProfilePlanLibrary | null => {
  if (!library) {
    return library;
  }

  return {
    ...library,
    sources: library.sources.map((source) =>
      source.source_type === targetType && source.source_id === targetId
        ? {
            ...source,
            plans: source.plans.filter((plan) => plan.id !== planId),
          }
        : source
    ),
  };
};

const updateLibrarySourceEnabled = (
  library: ProfilePlanLibrary | null,
  groupId: string,
  enabled: boolean
): ProfilePlanLibrary | null => {
  if (!library) {
    return library;
  }

  return {
    ...library,
    sources: library.sources.map((source) =>
      source.source_type === 'group' && source.source_id === groupId
        ? { ...source, enabled_for_profile: enabled }
        : source
    ),
  };
};

const updateLibraryAutoGeneration = (
  library: ProfilePlanLibrary | null,
  enabled: boolean
): ProfilePlanLibrary | null =>
  library
    ? {
        ...library,
        auto_generation_enabled: enabled,
      }
    : library;

const initialProfilePlansState = {
  profilePlanLibrary: null,
  profilePlanLibraryStatusFilter: DEFAULT_PROFILE_PLAN_LIBRARY_VIEW,
  profilePlanLibraryLoadedProfileId: null,
  profilePlanLibraryLoading: false,
  profilePlanLibrarySavingSourceId: null,
  profilePlanLibraryUpdatingPlanId: null,
  profilePlanLibraryArchivingPlanId: null,
  profilePlanLibrarySavingAutoGeneration: false,
  profilePlanLibraryError: null,
};

export const createProfilePlansStateStore: StateCreator<
  ProfilePlansState & Pick<GroupsState, 'deletedGroupIds'>,
  [],
  [],
  ProfilePlansState
> = (set, get) => {
  let nextRequestId = 0;

  return {
    ...initialProfilePlansState,

    fetchProfilePlanLibrary: async (
      profileId,
      view = DEFAULT_PROFILE_PLAN_LIBRARY_VIEW
    ) => {
      const requestId = ++nextRequestId;
      const profileChanged =
        get().profilePlanLibraryLoadedProfileId !== profileId;

      set({
        profilePlanLibraryStatusFilter: view,
        profilePlanLibraryLoading: true,
        profilePlanLibraryError: null,
        ...(profileChanged
          ? {
              profilePlanLibrary: null,
              profilePlanLibraryLoadedProfileId: null,
            }
          : {}),
      });

      try {
        const response = await listProfilePlanSources(
          profileId,
          profilePlanListRequest(view)
        );
        const library = filterDeletedGroupSources(
          response,
          get().deletedGroupIds
        );
        if (
          requestId !== nextRequestId ||
          library.profile.profile_id !== profileId
        ) {
          return library;
        }

        set({
          profilePlanLibrary: library,
          profilePlanLibraryLoadedProfileId: profileId,
          profilePlanLibraryLoading: false,
          profilePlanLibraryError: null,
        });
        return library;
      } catch (error) {
        if (requestId === nextRequestId) {
          set({
            profilePlanLibraryLoading: false,
            profilePlanLibraryError: 'Failed to fetch profile plan library',
          });
        }
        throw error;
      }
    },

    setProfilePlanAutoGeneration: async (profileId, enabled) => {
      const previousLibrary = get().profilePlanLibrary;
      set({
        profilePlanLibrary: updateLibraryAutoGeneration(
          previousLibrary,
          enabled
        ),
        profilePlanLibrarySavingAutoGeneration: true,
        profilePlanLibraryError: null,
      });

      try {
        const savedEnabled = await updateProfilePlanAutoGenerationSettings(
          profileId,
          enabled
        );
        set((state) => ({
          profilePlanLibrary: updateLibraryAutoGeneration(
            state.profilePlanLibrary,
            savedEnabled
          ),
          profilePlanLibrarySavingAutoGeneration: false,
        }));
        return savedEnabled;
      } catch (error) {
        const previousAutoGenerationEnabled =
          previousLibrary?.auto_generation_enabled ?? !enabled;
        set((state) => ({
          profilePlanLibrary:
            state.profilePlanLibrary?.profile.profile_id === profileId
              ? updateLibraryAutoGeneration(
                  state.profilePlanLibrary,
                  previousAutoGenerationEnabled
                )
              : state.profilePlanLibrary,
          profilePlanLibrarySavingAutoGeneration: false,
          profilePlanLibraryError:
            'Failed to save automatic plan generation setting',
        }));
        throw error;
      }
    },

    setProfileGroupPlanSourceEnabled: async (profileId, groupId, enabled) => {
      const previousLibrary = get().profilePlanLibrary;
      const previousSourceEnabled =
        previousLibrary?.sources.find(
          (source) =>
            source.source_type === 'group' && source.source_id === groupId
        )?.enabled_for_profile ?? !enabled;
      set({
        profilePlanLibrary: updateLibrarySourceEnabled(
          previousLibrary,
          groupId,
          enabled
        ),
        profilePlanLibrarySavingSourceId: groupId,
        profilePlanLibraryError: null,
      });

      try {
        await updateProfilePlanSourceEnabled(profileId, groupId, enabled);
      } catch (error) {
        set((state) => ({
          profilePlanLibrary:
            state.profilePlanLibrary?.profile.profile_id === profileId
              ? updateLibrarySourceEnabled(
                  state.profilePlanLibrary,
                  groupId,
                  previousSourceEnabled
                )
              : state.profilePlanLibrary,
          profilePlanLibraryError: 'Failed to update group plan source',
        }));
        throw error;
      } finally {
        if (get().profilePlanLibrarySavingSourceId === groupId) {
          set({ profilePlanLibrarySavingSourceId: null });
        }
      }
    },

    updateProfileLibraryPlanStatus: async (
      targetType,
      targetId,
      planId,
      status
    ) => {
      set({
        profilePlanLibraryUpdatingPlanId: planId,
        profilePlanLibraryError: null,
      });

      try {
        const updatedPlan =
          targetType === 'group'
            ? await updateGroupPlanStatus(targetId, planId, status)
            : await updateProfilePlanStatus(targetId, planId, status);
        set({ profilePlanLibraryUpdatingPlanId: null });
        return updatedPlan;
      } catch (error) {
        set({
          profilePlanLibraryUpdatingPlanId: null,
          profilePlanLibraryError: 'Failed to update plan status',
        });
        throw error;
      }
    },

    archiveProfileLibraryPlan: async (targetType, targetId, planId) => {
      set({
        profilePlanLibraryArchivingPlanId: planId,
        profilePlanLibraryError: null,
      });

      try {
        if (targetType === 'group') {
          await archiveGroupPlan(targetId, planId);
        } else {
          await archiveProfilePlan(targetId, planId);
        }
        set((state) => ({
          profilePlanLibrary: removeLibraryPlan(
            state.profilePlanLibrary,
            targetType,
            targetId,
            planId
          ),
          profilePlanLibraryArchivingPlanId: null,
        }));
      } catch (error) {
        set({
          profilePlanLibraryArchivingPlanId: null,
          profilePlanLibraryError: 'Failed to archive plan',
        });
        throw error;
      }
    },

    applyProfileLibraryPlan: (plan, view) =>
      set((state) => ({
        profilePlanLibrary: updateLibraryPlan(
          state.profilePlanLibrary,
          plan,
          view ?? state.profilePlanLibraryStatusFilter
        ),
      })),

    clearPlansState: () => {
      nextRequestId += 1;
      set(initialProfilePlansState);
    },
  };
};
